import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Instagram,
  Youtube,
  Linkedin,
  MoreVertical,
  Play,
  Pencil,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

interface PlatformStatus {
  platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
  accountId: string;
  status: 'pending' | 'scheduled' | 'posted' | 'failed';
  scheduledTime: string;
  postedAt?: string;
  platformPostId?: string;
  error?: string;
}

interface ScheduledPost {
  _id: string;
  projectId?: string;
  renderId?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  platforms: PlatformStatus[];
  title: string;
  caption: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

interface PostStatusCardProps {
  post: ScheduledPost;
  onEdit: (post: ScheduledPost) => void;
  onDelete: (postId: string) => void;
  onPublishNow: (postId: string) => void;
}

export const PostStatusCard: React.FC<PostStatusCardProps> = ({
  post,
  onEdit,
  onDelete,
  onPublishNow,
}) => {
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-4 w-4" />;
      case 'youtube':
        return <Youtube className="h-4 w-4" />;
      case 'linkedin':
        return <Linkedin className="h-4 w-4" />;
      case 'tiktok':
        return <TikTokIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Helper to determine if a scheduled post is missed (past scheduled time)
  const getEffectiveStatus = (p: PlatformStatus): string => {
    if (p.status === 'scheduled' && new Date(p.scheduledTime) < new Date()) {
      return 'missed';
    }
    return p.status;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      case 'scheduled':
        return (
          <Badge variant="default" className="gap-1 bg-blue-500">
            <Clock className="h-3 w-3" />
            Scheduled
          </Badge>
        );
      case 'missed':
        return (
          <Badge variant="default" className="gap-1 bg-orange-500">
            <AlertTriangle className="h-3 w-3" />
            Missed
          </Badge>
        );
      case 'posted':
        return (
          <Badge variant="default" className="gap-1 bg-green-500">
            <CheckCircle className="h-3 w-3" />
            Posted
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getOverallStatus = () => {
    const statuses = post.platforms.map((p) => getEffectiveStatus(p));
    if (statuses.every((s) => s === 'posted')) return 'posted';
    if (statuses.some((s) => s === 'failed')) return 'failed';
    if (statuses.some((s) => s === 'missed')) return 'missed';
    if (statuses.some((s) => s === 'scheduled')) return 'scheduled';
    return 'pending';
  };

  const getNextScheduledTime = () => {
    const now = new Date();
    // Get platforms that aren't posted or failed
    const pendingPlatforms = post.platforms
      .filter((p) => p.status === 'scheduled' || p.status === 'pending')
      .sort((a, b) => new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime());

    // Prefer future scheduled times
    const futurePlatform = pendingPlatforms.find(p => new Date(p.scheduledTime) > now);
    if (futurePlatform) {
      return futurePlatform.scheduledTime;
    }
    // Fall back to earliest time (even if past, for display purposes)
    return pendingPlatforms[0]?.scheduledTime;
  };

  const overallStatus = getOverallStatus();
  const nextTime = getNextScheduledTime();
  const canPublishNow = post.platforms.some(
    (p) => p.status === 'pending' || p.status === 'scheduled'
  );

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex gap-4 p-4">
          {/* Thumbnail */}
          <div className="flex-shrink-0">
            {post.thumbnailUrl ? (
              <img
                src={post.thumbnailUrl}
                alt={post.title}
                className="w-32 h-20 object-cover rounded-lg"
              />
            ) : (
              <div className="w-32 h-20 bg-muted rounded-lg flex items-center justify-center">
                <Play className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold truncate">{post.title || 'Untitled Post'}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                  {post.caption || 'No caption'}
                </p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(post)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  {canPublishNow && (
                    <DropdownMenuItem onClick={() => onPublishNow(post._id)}>
                      <Play className="h-4 w-4 mr-2" />
                      Publish Now
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onDelete(post._id)}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Platform Status */}
            <div className="flex flex-wrap gap-2 mt-3">
              {post.platforms.map((platform, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-sm bg-muted/50 rounded-full px-3 py-1"
                >
                  {getPlatformIcon(platform.platform)}
                  {getStatusBadge(getEffectiveStatus(platform))}
                  {platform.error && (
                    <span className="text-xs text-destructive truncate max-w-[150px]">
                      {platform.error}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Schedule Time */}
            {nextTime && overallStatus !== 'posted' && (
              <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>
                  {new Date(nextTime) > new Date()
                    ? `Scheduled for ${format(new Date(nextTime), 'MMM d, yyyy h:mm a')}`
                    : `Was scheduled for ${format(new Date(nextTime), 'MMM d, yyyy h:mm a')}`
                  }
                </span>
              </div>
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {post.tags.slice(0, 3).map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
                {post.tags.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{post.tags.length - 3} more
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
